import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { listTeam, updateUserRole, inviteTeamMember, removeTeamMember, listCrew } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Shield, UserPlus, Trash2, Crown, Briefcase, HardHat } from "lucide-react";

const ROLE_ICONS = { owner: Crown, manager: Briefcase, crew: HardHat };
const ROLE_COLORS = { owner: "role-badge-owner", manager: "role-badge-manager", crew: "role-badge-crew" };

export default function TeamPage() {
  const { user, role } = useOutletContext();
  const [team, setTeam] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "crew", crew_id: "" });

  const fetchTeam = useCallback(async () => {
    try {
      const [teamRes, crewRes] = await Promise.all([listTeam(), listCrew()]);
      setTeam(teamRes.data);
      setCrewList(crewRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load team");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success("Role updated");
      fetchTeam();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) { toast.error("Name and email required"); return; }
    try {
      await inviteTeamMember(inviteForm);
      toast.success("Team member added");
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "crew", crew_id: "" });
      fetchTeam();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invite failed"); }
  };

  const handleRemove = async (userId) => {
    try {
      await removeTeamMember(userId);
      toast.success("Member removed");
      fetchTeam();
    } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
  };

  const initials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="team-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Team & Permissions</h1>
          <p className="text-muted-foreground text-sm mt-1">{team.length} members &middot; Manage roles & access</p>
        </div>
        <Button data-testid="invite-member-btn" onClick={() => setInviteOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider rounded-sm">
          <UserPlus className="w-3.5 h-3.5 mr-2" /> Add Member
        </Button>
      </div>

      {/* Role Legend */}
      <Card className="border-border">
        <CardContent className="p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Permission Levels</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-3 bg-secondary/20 rounded-sm border border-border">
              <div className="flex items-center gap-2 mb-1.5"><Crown className="w-4 h-4 text-amber-400" /><span className="text-sm font-semibold text-amber-400">Owner</span></div>
              <p className="text-xs text-muted-foreground">Full access. Revenue, rates, clients, CRUD on all data, team management.</p>
            </div>
            <div className="p-3 bg-secondary/20 rounded-sm border border-border">
              <div className="flex items-center gap-2 mb-1.5"><Briefcase className="w-4 h-4 text-blue-400" /><span className="text-sm font-semibold text-blue-400">Manager</span></div>
              <p className="text-xs text-muted-foreground">Add/edit gear & crew. View events. No revenue, pay rates, or client financials.</p>
            </div>
            <div className="p-3 bg-secondary/20 rounded-sm border border-border">
              <div className="flex items-center gap-2 mb-1.5"><HardHat className="w-4 h-4 text-zinc-400" /><span className="text-sm font-semibold text-zinc-400">Crew</span></div>
              <p className="text-xs text-muted-foreground">View-only on own assignments. No financial data, no create/edit/delete.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team List */}
      <div className="space-y-2">
        {team.map(member => {
          const RIcon = ROLE_ICONS[member.role] || HardHat;
          const isMe = member.user_id === user?.user_id;
          return (
            <Card key={member.user_id} data-testid={`team-member-${member.user_id}`} className="border-border hover:border-primary/20 transition-colors">
              <CardContent className="p-4 flex items-center gap-4">
                <Avatar className="w-10 h-10 shrink-0">
                  <AvatarImage src={member.picture} alt={member.name} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials(member.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{member.name}</p>
                    {isMe && <Badge variant="outline" className="text-xs">You</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {isMe ? (
                    <Badge className={`${ROLE_COLORS[member.role]} border text-xs`}>
                      <RIcon className="w-3 h-3 mr-1" />{member.role}
                    </Badge>
                  ) : (
                    <Select value={member.role} onValueChange={(v) => handleRoleChange(member.user_id, v)}>
                      <SelectTrigger data-testid={`role-select-${member.user_id}`} className="w-32 h-8 text-xs bg-secondary/30 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="crew">Crew</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {!isMe && (
                    <Button data-testid={`remove-member-${member.user_id}`} variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => handleRemove(member.user_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Add Team Member</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Pre-register a team member. They will inherit this role on first login.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Name</Label><Input data-testid="invite-name-input" value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} className="bg-secondary/30 border-border" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Email</Label><Input data-testid="invite-email-input" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} className="bg-secondary/30 border-border" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Role</Label>
              <Select value={inviteForm.role} onValueChange={v => setInviteForm({...inviteForm, role: v})}>
                <SelectTrigger data-testid="invite-role-select" className="bg-secondary/30 border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="crew">Crew</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteForm.role === "crew" && crewList.length > 0 && (
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Link to Crew Profile (optional)</Label>
                <Select value={inviteForm.crew_id || "none"} onValueChange={v => setInviteForm({...inviteForm, crew_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="invite-crew-link-select" className="bg-secondary/30 border-border"><SelectValue placeholder="Select crew member" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {crewList.map(c => <SelectItem key={c.crew_id} value={c.crew_id}>{c.name} - {c.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="invite-cancel-btn" variant="secondary" onClick={() => setInviteOpen(false)} className="text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="invite-save-btn" onClick={handleInvite} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
